import { planName, PlanType } from '@lib/billing-plan-name';
import { BillingInfoPlan, OrgInfoSubscriptionType } from '@nx-frontend/shared/types';
import { OrgInfo, BillingInfo } from '@nx-frontend/shared/types';
import { OrgCoupons } from '@nx-frontend/shared/types';
import { hasAppsumoBundle } from './appsumo';

export interface IGetSubscriptionTypeParams {
  showMemberMessage?: boolean;
  memberMessage?: string;
  memberOkText?: string;
}

export type IGetSubscriptionType =
  | 'standard'
  | 'premium'
  | 'premiumTrialWithCard'
  | 'plus'
  | 'plusTrialWithCard'
  | 'business'
  | 'businessTrialWithCard'
  | 'team'
  | 'teamTrialWithCard'
  | 'business2023'
  | 'business2023TrialWithCard'
  | 'plus2024'
  | 'plus2024TrialWithCard'
  | 'team2024'
  | 'team2024TrialWithCard'
  | 'business2024'
  | 'business2024TrialWithCard';

export interface IGetSubscriptionTypeStore {
  orgInfo: OrgInfo;
  orgCoupons: OrgCoupons | null;
  billingInfo: BillingInfo;
}

/**
 * Get current org subscription type include trial with card
 * @param params
 * @returns
 */
export const getSubscriptionType = (store: IGetSubscriptionTypeStore): IGetSubscriptionType => {
  const { orgInfo, orgCoupons, billingInfo } = store;

  let currentType = orgInfo.subscriptionType as string;

  // check do user have active trial with linked card or business trial or LTD user
  if (
    billingInfo.plan === 'trial' ||
    !billingInfo.plan ||
    billingInfo?.inheritedFromOrgs?.length > 0 ||
    billingInfo.active === false
  ) {
    currentType = OrgInfoSubscriptionType.STANDARD; // free
  }

  // when user have active trial with linked card and trial not expired yet
  // always show a limit business popup
  const now = Math.floor(Date.now() / 1000);
  if (billingInfo.active && billingInfo.trialEndsAt && now < billingInfo.trialEndsAt) {
    currentType = currentType + 'TrialWithCard'; // allow to buy addon for tial with card
  }

  const isAppsumoBundle = hasAppsumoBundle(billingInfo, orgCoupons);
  if (isAppsumoBundle && billingInfo && orgInfo) {
    if (!billingInfo.orgId && orgInfo.orgId) {
      currentType = orgInfo.subscriptionType;
    }
  }

  return currentType as IGetSubscriptionType;
};

export const hasMaxSubscriptionPlan = (orgInfo: OrgInfo, billingInfo: BillingInfo, orgCoupons: OrgCoupons) => {
  const subscriptionType: IGetSubscriptionType | string = getSubscriptionType({ orgInfo, orgCoupons, billingInfo });
  return (
    subscriptionType === OrgInfoSubscriptionType.BUSINESS ||
    subscriptionType === OrgInfoSubscriptionType.BUSINESS2023 ||
    subscriptionType === OrgInfoSubscriptionType.BUSINESS2024 ||
    subscriptionType === OrgInfoSubscriptionType.BUSINESS + 'TrialWithCard' ||
    subscriptionType === OrgInfoSubscriptionType.BUSINESS2023 + 'TrialWithCard' ||
    subscriptionType === OrgInfoSubscriptionType.BUSINESS2024 + 'TrialWithCard'
  );
};

export const mapSubscriptionType = (subscriptionType: IGetSubscriptionType | string): IGetSubscriptionType => {
  const subscriptionTypeMap: Record<string, IGetSubscriptionType> = {
    premium: 'plus',
    premiumTrialWithCard: 'plus',
    plusTrialWithCard: 'plus',
    business: 'team',
    businessTrialWithCard: 'team',
    teamTrialWithCard: 'team',
    business2023TrialWithCard: 'business2023',
    team2024TrialWithCard: 'team2024',
    plus2024TrialWithCard: 'plus2024',
    business2024TrialWithCard: 'business2024',
  };
  return subscriptionTypeMap[subscriptionType] || subscriptionType;
};

/*
  mapPlanToSubscription function returns a human-readable subscription type
*/
export const mapPlanToSubscription = (billingInfo: BillingInfo) => {
  const plan = billingInfo?.plan;

  let subscriptionType = '';
  let subscriptionPeriod = '';

  if ([BillingInfoPlan.PLUS_MONTHLY, BillingInfoPlan.PLUS_YEARLY].includes(plan)) {
    subscriptionType = 'Standard';
  } else if ([BillingInfoPlan.PLUS2024_MONTHLY, BillingInfoPlan.PLUS2024_YEARLY].includes(plan)) {
    subscriptionType = planName[PlanType.PLUS];
  } else if (
    [
      BillingInfoPlan.TEAM_MONTHLY,
      BillingInfoPlan.TEAM_YEARLY,
      BillingInfoPlan.TEAM2024_MONTHLY,
      BillingInfoPlan.TEAM2024_YEARLY,
    ].includes(plan)
  ) {
    subscriptionType = planName[PlanType.TEAM];
  } else if (
    [
      BillingInfoPlan.BUSINESS_MONTHLY,
      BillingInfoPlan.BUSINESS_YEARLY,
      BillingInfoPlan.BUSINESS2024_MONTHLY,
      BillingInfoPlan.BUSINESS2024_YEARLY,
    ].includes(plan)
  ) {
    subscriptionType = planName[PlanType.BUSINESS];
  }
  if (
    [
      BillingInfoPlan.PLUS_MONTHLY,
      BillingInfoPlan.PLUS2024_MONTHLY,
      BillingInfoPlan.TEAM_MONTHLY,
      BillingInfoPlan.TEAM2024_MONTHLY,
      BillingInfoPlan.BUSINESS_MONTHLY,
      BillingInfoPlan.BUSINESS2023_MONTHLY,
      BillingInfoPlan.BUSINESS2024_MONTHLY,
    ].includes(plan)
  ) {
    subscriptionPeriod = 'monthly';
  } else {
    subscriptionPeriod = 'yearly';
  }

  return {
    type: subscriptionType,
    period: subscriptionPeriod,
  };
};
